'use client';

/* ──────────────────────────────────────────────
 *  GiftCardUnavailable — état vide convivial affiché
 *  quand aucun achat de carte cadeau n'est possible
 *  (pro qui ne propose pas de carte cadeau, ou config
 *  indisponible). Remplace le simple texte brut.
 *  Thémé via les variables `--bt-*` du tunnel.
 * ────────────────────────────────────────────── */

import React from 'react';
import { MAPPED_TRANSLATION } from '@cfixe-monorepo/shared';

const T =
  (MAPPED_TRANSLATION['fr-FR'] as unknown as Record<string, Record<string, string>>)['GIFT_CARD_PUBLIC_PAGE'] ?? {};
const t = (key: string): string => T[key] ?? key;

const wrap: React.CSSProperties = {
  maxWidth: 460,
  margin: '0 auto',
  padding: '56px 20px',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  alignItems: 'center',
};
const iconCircle: React.CSSProperties = {
  width: 76,
  height: 76,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--bt-light, #eef1f6)',
  color: 'var(--bt-accent, #2563eb)',
  marginBottom: 6,
};

export function GiftCardUnavailable({
  professionalName,
  scope = 'full',
}: {
  professionalName?: string;
  /** `pack` = page « Packs » → copie + icône adaptées (le tunnel est réutilisé tel quel). */
  scope?: 'full' | 'pack';
}): React.JSX.Element {
  const isPack = scope === 'pack';
  return (
    <div className="bt-root">
      <div style={wrap}>
        <div style={iconCircle}>{isPack ? <PackageIcon /> : <GiftIcon />}</div>
        <h2 className="bt-step-header__title">{t(isPack ? 'UNAVAILABLE_TITLE_PACK' : 'UNAVAILABLE_TITLE')}</h2>
        <p className="bt-step-header__subtitle" style={{ lineHeight: 1.5 }}>
          {t(isPack ? 'UNAVAILABLE_DESC_PACK' : 'UNAVAILABLE_DESC')}
        </p>
        {professionalName && <p className="bt-step-header__subtitle">{professionalName}</p>}
      </div>
    </div>
  );
}

function GiftIcon(): React.JSX.Element {
  return (
    <svg
      width="34"
      height="34"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="8" width="18" height="4" rx="1" />
      <path d="M12 8v13" />
      <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
      <path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8" />
      <path d="M16.5 8a2.5 2.5 0 0 0 0-5C13 3 12 8 12 8" />
    </svg>
  );
}

function PackageIcon(): React.JSX.Element {
  return (
    <svg
      width="34"
      height="34"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M16.5 9.4 7.5 4.21" />
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </svg>
  );
}
